import Button from "../../../../../../common/components/xepp/button/Button";
import styles from "./ServiceTypeFormInformation.module.scss";
import { ReactComponent as TrashCanIcon } from "../../../../../../assets/icons/trash-can.svg";
import { DragIndicatorIcon, DownArrowIcon, PenIcon, UpArrowIcon, ThreeDotsIcon } from "../../../../../../assets/icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../../stores/store";
import { Attribute, getFieldTypeString } from "../../../../../../model/service-request/attribute";
import ToggleButton from "../../../../../../common/components/toggle-button/ToggleButton";
import {
	changeOrderOfAttributesInStepAdditionalServiceRequest,
	changeOrderOfSectionsInStepAdditionalServiceRequest,
	enableFieldInStepAdditionalServiceRequest,
	removeSectionFromStepAdditionalServiceRequest,
	removeFieldFromStepAdditionalServiceRequest,
} from "../../../../../../stores/slices/workflow-slice";
import { changeOrderOfAttributes, changeOrderOfSections, enableField, removeSection } from "../../../../../../stores/slices/service-type-slice";
import { removeField } from "../../../../../../stores/slices/service-type-slice";
import { useRef } from "react";
import { SectionDefinition } from "../../../../../../model/service-request/section-definition";
import { useState } from "react";
import FieldTypeModal from "../FieldModal/FieldTypeModal";
import NewSectionModal from "../NewSectionModal/NewSectionModal";
import DeleteSectionModal from "../DeleteModal/DeleteModal";
import { StepDefinition } from "../../../../../../model/workflows/step-definition";

interface DragableField {
	index: number;
	field?: Attribute;
}

export interface ServiceTypeFormInformationProps {
	step?: StepDefinition;
}

const ServiceTypeFormInformation = (props: ServiceTypeFormInformationProps) => {
	const dispatch = useDispatch();
	const globalState = useSelector((state: RootState) => state.serviceType);
	const currentState = props.step ? props.step.additionalServiceRequest : globalState;

	const dragSection = useRef<DragableField>({ index: 0 });
	const draggedOverSection = useRef<DragableField>({ index: 0 });
	const [isAttributeDraggStart, setIsAttributeDraggStart] = useState(false);

	const StartAttributeDraging = () => {
		setIsAttributeDraggStart(true);
	};
	const EndAttrinbuteDragging = () => {
		setIsAttributeDraggStart(false);
	};

	function handleSectionSort() {
		if (!currentState) return;
		var newSections = [...currentState.sectionDefinitions];

		const draggedItem = newSections[dragSection.current.index];
		// remove the dragged item
		newSections.splice(dragSection.current.index, 1);
		// insert it at the new position
		newSections.splice(draggedOverSection.current.index, 0, draggedItem);

		newSections = newSections.map((x, i) => {
			return { ...x, displayOrder: i + 1 };
		});
		if (props.step) {
			dispatch(changeOrderOfSectionsInStepAdditionalServiceRequest({ stepId: props.step.id, sections: newSections }));
		} else {
			dispatch(changeOrderOfSections({ sections: newSections }));
		}
	}

	if (!currentState) {
		return null;
	}

	return (
		<div className={styles.formSections}>
			{currentState.sectionDefinitions.map((section, index) => {
				return (
					<div
						key={`section-${section.id}`}
						draggable
						onDragStart={() => {
							dragSection.current = { index: index };
						}}
						onDragEnter={() => (draggedOverSection.current = { index: index })}
						onDragEnd={() => {
							if (!isAttributeDraggStart) handleSectionSort();
						}}
						onDragOver={e => {
							e.preventDefault();
						}}
					>
						<SectionInformation
							step={props.step}
							section={section}
							sectionIndex={index}
							startAttributeDragging={StartAttributeDraging}
							endAttributeDragging={EndAttrinbuteDragging}
						/>
					</div>
				);
			})}
		</div>
	);
};

export interface SectionInformationProps {
	step?: StepDefinition;
	section: SectionDefinition;
	sectionIndex: number;
	startAttributeDragging: () => void;
	endAttributeDragging: () => void;
}

const SectionInformation: React.FC<SectionInformationProps> = ({ step, section, sectionIndex, startAttributeDragging, endAttributeDragging }) => {
	const dispatch = useDispatch();
	const [isExpanded, setIsExpanded] = useState(true);

	const [showFieldModal, setShowFiledModal] = useState(false);
	const [existingAttribute, setExistingAttribute] = useState<Attribute | undefined>(undefined);

	const [showEditSectionNameModal, setShowEditSectionNameModal] = useState(false);
	const [existingSection, setExistingSection] = useState<SectionDefinition | undefined>(undefined);

	const [showSectionOptions, setShowSectionOptions] = useState(false);
	const [showDeleteSectionModal, setShowDeleteSectionModal] = useState(false);
	const [showDeleteAttributeModal, setShowDeleteAttributeModal] = useState(false);

	const dragAttribute = useRef<DragableField>({ index: 0 });
	const draggedOverAttribute = useRef<DragableField>({ index: 0 });

	function handleSort(section: SectionDefinition, sectionIndex: number) {
		var newAttributes = [...section.attributeDefinitions];

		const draggedItem = newAttributes[dragAttribute.current.index];
		// remove the dragged item
		newAttributes.splice(dragAttribute.current.index, 1);
		// insert it at the new position
		newAttributes.splice(draggedOverAttribute.current.index, 0, draggedItem);

		newAttributes = newAttributes.map((x, i) => {
			return { ...x, displayOrder: i + 1 };
		});
		if (step) {
			dispatch(changeOrderOfAttributesInStepAdditionalServiceRequest({ stepId: step.id, sectionIndex: sectionIndex, attributes: newAttributes }));
		} else {
			dispatch(changeOrderOfAttributes({ sectionIndex: sectionIndex, attributes: newAttributes }));
		}
	}

	const openFieldTypeModal = () => {
		setExistingAttribute(undefined);
		setShowFiledModal(true);
	};

	const editFieldTypeModal = (attr: Attribute) => {
		setExistingAttribute(attr);
		setShowFiledModal(true);
	};

	const editSectionNameModal = (section: SectionDefinition) => {
		setExistingSection(section);
		setShowEditSectionNameModal(true);
	};

	const onRemoveClick = (attribute: Attribute) => {
		setExistingAttribute(attribute);
		setShowDeleteAttributeModal(true);
	};

	const onRemoveSctionClick = (section: SectionDefinition) => {
		setExistingSection(section);
		setShowDeleteSectionModal(true);
	};

	return (
		<>
			{showFieldModal && (
				<FieldTypeModal
					stepId={step?.id}
					onModalClose={() => {
						setShowFiledModal(false);
						setIsExpanded(true);
					}}
					existingAttribute={existingAttribute}
					sectionIndex={sectionIndex!}
				/>
			)}
			{showEditSectionNameModal && (
				<NewSectionModal
					stepId={step?.id}
					onModalClose={() => {
						setShowEditSectionNameModal(false);
						setShowSectionOptions(false);
					}}
					section={existingSection}
				/>
			)}
			{showDeleteSectionModal && (
				<DeleteSectionModal
					onModalClose={() => {
						setShowDeleteSectionModal(false);
						setShowSectionOptions(false);
					}}
					onDeleteClick={() => {
						if (step) {
							dispatch(removeSectionFromStepAdditionalServiceRequest({ stepId: step.id, section: existingSection! }));
						} else {
							dispatch(removeSection(existingSection!));
						}
					}}
					title="Delete Section"
					message={`Are you sure you want to delete "${existingSection?.name}" ?`}
				/>
			)}
			{showDeleteAttributeModal && (
				<DeleteSectionModal
					onModalClose={() => {
						setShowDeleteAttributeModal(false);
						setShowSectionOptions(false);
					}}
					onDeleteClick={() => {
						if (step) {
							dispatch(removeFieldFromStepAdditionalServiceRequest({ stepId: step.id, attribute: existingAttribute!, sectionIndex: sectionIndex }));
						} else {
							dispatch(removeField({ attribute: existingAttribute!, sectionIndex: sectionIndex }));
						}
					}}
					title="Delete Attribute"
					message={`Are you sure you want to delete "${existingAttribute?.label}" in "${section.name}"  ?`}
				/>
			)}

			<div className={styles.formInfoContainer}>
				<div
					className={styles.headerSection}
					onClick={() => {
						setIsExpanded(!isExpanded);
						setShowSectionOptions(false);
					}}
				>
					<div className={styles.sectionName}>
						<span>{section!.name}</span>
						<ThreeDotsIcon
							className={styles.ThreeDotsIcon}
							onClick={e => {
								e.stopPropagation();
								setShowSectionOptions(!showSectionOptions);
							}}
						/>
						{showSectionOptions && (
							<div className={styles.sectionOptions}>
								<Button
									text="Rename Section"
									leftIcon={<PenIcon className={styles.leftIcon} />}
									variant="outline"
									onClick={e => {
										e.stopPropagation();
										editSectionNameModal(section);
									}}
								/>

								<Button
									text="Delete Section"
									leftIcon={<TrashCanIcon className={styles.leftIcon} />}
									variant="outline"
									onClick={e => {
										e.stopPropagation();
										onRemoveSctionClick(section);
									}}
								/>
							</div>
						)}
					</div>
					<div className={styles.sectionOptions}>
						<div className={styles.totalAddedFields}>
							<span className={styles.label}>Total Added Fields</span>
							<span>{section!.attributeDefinitions.length}</span>
						</div>
						<div>
							<Button text="Add New Field" variant="outline" onClick={openFieldTypeModal} />
						</div>
						<div className={styles.expandIcon}>{isExpanded ? <UpArrowIcon /> : <DownArrowIcon />}</div>
					</div>
				</div>
				{isExpanded && (
					<div className={styles.contentSection} key={`section-content-${section?.id}`}>
						{section!.attributeDefinitions.map((field, index) => (
							<div
								key={index}
								className={styles.fieldRow}
								draggable
								onDragStart={e => {
									startAttributeDragging();
									dragAttribute.current = { field: field, index: index };
								}}
								onDragEnter={() => {
									draggedOverAttribute.current = { field: field, index: index };
								}}
								onDragEnd={e => {
									handleSort(section, sectionIndex);
									endAttributeDragging();
								}}
								onDragOver={e => {
									e.preventDefault();
								}}
							>
								<div className={styles.fieldNameWrapper}>
									{section.attributeDefinitions.length > 1 && <DragIndicatorIcon className={styles.icon} />}
									<span>{field.label}</span>
								</div>
								<div className={styles.actionsWrapper}>
									<Button text={getFieldTypeString(field.fieldType!)} variant="danger" />
									<ToggleButton
										value={field.isEnabled}
										onToggle={() => {
											if (step) {
												dispatch(
													enableFieldInStepAdditionalServiceRequest({
														stepId: step.id,
														attribute: field,
														enabled: !field.isEnabled,
														sectionIndex: sectionIndex!,
													})
												);
											} else {
												dispatch(
													enableField({
														attribute: field,
														enabled: !field.isEnabled,
														sectionIndex: sectionIndex!,
													})
												);
											}
										}}
									/>
									<Button
										text=""
										leftIcon={<PenIcon />}
										variant="outline"
										onClick={() => {
											editFieldTypeModal(field);
										}}
									/>
									<Button
										text=""
										leftIcon={<TrashCanIcon />}
										variant="outline"
										onClick={() => {
											onRemoveClick(field);
										}}
									/>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</>
	);
};

export default ServiceTypeFormInformation;
